import { useMemo, useState } from "react";

import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import ListSubheader from "@material-ui/core/ListSubheader";
import List, { ListProps } from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import IconButton from "@material-ui/core/IconButton";
import EditIcon from "@material-ui/icons/Edit";
import SaveIcon from "@material-ui/icons/Save";
import TextFormatIcon from "@material-ui/icons/TextFormat";
import BuildIcon from "@material-ui/icons/Build";
import ImportExportIcon from "@material-ui/icons/ImportExport";
import CategoryIcon from "@material-ui/icons/Category";

import {
  Apartment,
  Device,
  DeviceApartment,
  DeviceType,
  DeviceBuilding,
  DeviceRoom,
  DomoticsProvider,
  Room,
} from "../../../graphql/models";
import { useSnackbarMessage, useSnackbarMutation } from "../../../hooks";
import { MUTATIONS } from "../../../graphql";

import useDeviceEditorStyles from "./device_editor.style";

export interface DeviceEditorProps extends ListProps {
  apartment: Apartment;
  room: Room;
  device: Device;
  onDeviceUpdated?: (updatedDevice: Device) => void;
  askRoom?: boolean;
  askApartment?: boolean;
  askBuilding?: boolean;
}

export function DeviceEditor(props: DeviceEditorProps) {
  const { apartment, device, room, onDeviceUpdated, ...listProps } = props;

  const classes = useDeviceEditorStyles();
  const { raiseSuccessSnackbar } = useSnackbarMessage();

  const [invokeUpdateDevice] = useSnackbarMutation(MUTATIONS.UPDATE_DEVICE, {
    onCompleted: () => {
      setIsReadOnly(true);
      if (onDeviceUpdated) {
        onDeviceUpdated({ ...currentDeviceData });
      }
      raiseSuccessSnackbar(`Dispositivo aggiornato`);
    },
  });

  const [isReadOnly, setIsReadOnly] = useState(true);
  const toggleEditMode = () => {
    if (isReadOnly) {
      setIsReadOnly(false);
    } else {
      updateDevice();
    }
  };

  const onFormSubmit = (evt) => {
    evt.preventDefault();
    if (!isReadOnly) {
      updateDevice();
    }
  };

  const updateDevice = () => {
    invokeUpdateDevice({
      variables: {
        id: device?.id,
        deviceData: {
          ...currentDeviceData,
        },
      },
    });
  };

  const askWiserData = useMemo(() => {
    return apartment?.domoticsProvider === DomoticsProvider.SCHNEIDER_ELECTRIC;
  }, [apartment]);

  const [name, setName] = useState<string>(device?.name);
  const [type, setType] = useState<DeviceType>(device?.type);
  const [categoryName, setCategoryName] = useState<string>(
    device?.categoryName
  );
  const [deviceRoom] = useState<DeviceRoom>(
    device?.room ?? {
      id: room.id as string,
      name: room.name,
    }
  );
  const [deviceApartment] = useState<DeviceApartment>(
    device?.apartment ?? {
      id: apartment.id,
      name: apartment.name,
      code: apartment.code,
    }
  );
  const [building] = useState<DeviceBuilding>(device?.building);
  // Wiser opts
  const [getLightAddress, setGetLightAddress] = useState<string | undefined>(
    device?.wiserOpts?.getLightAddress
  );
  const [setLightAddress, setSetLightAddress] = useState<string | undefined>(
    device?.wiserOpts?.setLightAddress
  );
  const [getDimmerAddress, setGetDimmerAddress] = useState<string | undefined>(
    device?.wiserOpts?.getDimmerAddress
  );
  const [setDimmerAddress, setSetDimmerAddress] = useState<string | undefined>(
    device?.wiserOpts?.setDimmerAddress
  );
  const [getStatusAddress, setGetStatusAddress] = useState<string | undefined>(
    device?.wiserOpts?.getStatusAddress
  );
  const [setStatusAddress, setSetStatusAddress] = useState<string | undefined>(
    device?.wiserOpts?.setStatusAddress
  );
  const [getMoveAddress, setGetMoveAddress] = useState<string | undefined>(
    device?.wiserOpts?.getMoveAddress
  );
  const [setMoveAddress, setSetMoveAddress] = useState<string | undefined>(
    device?.wiserOpts?.setMoveAddress
  );
  const [setFanAddress, setSetFanAddress] = useState<string | undefined>(
    device?.wiserOpts?.setFanAddress
  );
  const [setTemperatureAddressSummer, setSetTemperatureAddressSummer] =
    useState<string | undefined>(
      device?.wiserOpts?.setTemperatureAddressSummer
    );
  const [setTemperatureAddressWinter, setSetTemperatureAddressWinter] =
    useState<string | undefined>(
      device?.wiserOpts?.setTemperatureAddressWinter
    );

  const currentDeviceData = useMemo(() => {
    const deviceData = { ...(device ?? {}) };
    delete deviceData.lightValue;
    delete deviceData.dimmerLightValue;
    delete deviceData.shutterDirectionValue;
    delete deviceData.shutterStatusValue;
    delete deviceData.shutterEffectiveStatus;

    return {
      ...deviceData,
      name,
      type,
      categoryName,
      room: deviceRoom,
      apartment: deviceApartment,
      building: building,
      wiserOpts: {
        getLightAddress,
        setLightAddress,
        getDimmerAddress,
        setDimmerAddress,
        getStatusAddress,
        setStatusAddress,
        getMoveAddress,
        setMoveAddress,
        setFanAddress,
        setTemperatureAddressSummer,
        setTemperatureAddressWinter,
      },
    };
  }, [
    building,
    categoryName,
    device,
    deviceApartment,
    deviceRoom,
    getDimmerAddress,
    getLightAddress,
    getStatusAddress,
    name,
    setDimmerAddress,
    setFanAddress,
    setLightAddress,
    setStatusAddress,
    getMoveAddress,
    setMoveAddress,
    setTemperatureAddressSummer,
    setTemperatureAddressWinter,
    type,
  ]);

  return (
    <form noValidate autoComplete="off" onSubmit={onFormSubmit}>
      <List
        {...listProps}
        component="nav"
        aria-labelledby={`device-${device?.id}-data-list-subheader`}
        subheader={
          <>
            <ListSubheader
              component="div"
              id={`room-${device?.id}-data-subheader`}
            >
              Proprietà dispositivo
              <ListItemSecondaryAction>
                <IconButton
                  onClick={toggleEditMode}
                  edge="end"
                  aria-label="edit"
                >
                  {isReadOnly ? <EditIcon /> : <SaveIcon />}
                </IconButton>
              </ListItemSecondaryAction>
            </ListSubheader>
          </>
        }
        style={{ width: "100%" }}
      >
        <>
          <ListItem>
            <ListItemIcon>
              <TextFormatIcon />
            </ListItemIcon>
            <TextField
              className={classes.textField}
              variant="outlined"
              defaultValue={name}
              onChange={(evt) => setName(evt.target.value)}
              disabled={isReadOnly}
              placeholder="Nome da visualizzare"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <BuildIcon />
            </ListItemIcon>
            <Select
              required
              label="Tipologia dispositivo"
              variant="outlined"
              className={classes.textField}
              disabled={isReadOnly}
              value={type}
              onChange={(e) => setType(e.target.value as DeviceType)}
            >
              <MenuItem value={DeviceType.LIGHT}>Luce</MenuItem>
              <MenuItem value={DeviceType.DIMMER_LIGHT}>Luce Dimmer</MenuItem>
              <MenuItem value={DeviceType.FAN}>Climatizzatore</MenuItem>
              <MenuItem value={DeviceType.SHUTTER}>Tapparella</MenuItem>
            </Select>
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CategoryIcon />
            </ListItemIcon>
            <Select
              required
              label="Categoria dispositivo"
              variant="outlined"
              className={classes.textField}
              disabled={isReadOnly}
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value as string)}
            >
              {/* <MenuItem value={'Clima'}>Clima</MenuItem> */}
              <MenuItem value={"luci"}>Illuminazione</MenuItem>
              <MenuItem value={"tapparelle"}>Tapparelle</MenuItem>
            </Select>
          </ListItem>
          {askWiserData && (
            <>
              <ListSubheader component="li">
                Dati per elettronica WISER
              </ListSubheader>
              {type === DeviceType.LIGHT && (
                <>
                  <ListItem>
                    <ListItemIcon>
                      <ImportExportIcon />
                    </ListItemIcon>
                    <TextField
                      className={classes.textField}
                      variant="outlined"
                      defaultValue={getLightAddress}
                      onChange={(evt) => setGetLightAddress(evt.target.value)}
                      disabled={isReadOnly}
                      label="Indirizzo GET stato luce"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <ImportExportIcon />
                    </ListItemIcon>
                    <TextField
                      className={classes.textField}
                      variant="outlined"
                      defaultValue={setLightAddress}
                      onChange={(evt) => setSetLightAddress(evt.target.value)}
                      disabled={isReadOnly}
                      label="Indirizzo SET stato luce"
                    />
                  </ListItem>
                </>
              )}
              {type === DeviceType.DIMMER_LIGHT && (
                <>
                  <ListItem>
                    <ListItemIcon>
                      <ImportExportIcon />
                    </ListItemIcon>
                    <TextField
                      className={classes.textField}
                      variant="outlined"
                      defaultValue={getDimmerAddress}
                      onChange={(evt) => setGetDimmerAddress(evt.target.value)}
                      disabled={isReadOnly}
                      label="Indirizzo GET stato luce dimmer"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <ImportExportIcon />
                    </ListItemIcon>
                    <TextField
                      className={classes.textField}
                      variant="outlined"
                      defaultValue={setDimmerAddress}
                      onChange={(evt) => setSetDimmerAddress(evt.target.value)}
                      disabled={isReadOnly}
                      label="Indirizzo SET stato luce dimmer"
                    />
                  </ListItem>
                </>
              )}
              {type === DeviceType.SHUTTER && (
                <>
                  <ListItem>
                    <ListItemIcon>
                      <ImportExportIcon />
                    </ListItemIcon>
                    <TextField
                      className={classes.textField}
                      variant="outlined"
                      defaultValue={getStatusAddress}
                      onChange={(evt) => setGetStatusAddress(evt.target.value)}
                      disabled={isReadOnly}
                      label="Indirizzo GET stato tapparella"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <ImportExportIcon />
                    </ListItemIcon>
                    <TextField
                      className={classes.textField}
                      variant="outlined"
                      defaultValue={setStatusAddress}
                      onChange={(evt) => setSetStatusAddress(evt.target.value)}
                      disabled={isReadOnly}
                      label="Indirizzo SET stato tapparella"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <ImportExportIcon />
                    </ListItemIcon>
                    <TextField
                      className={classes.textField}
                      variant="outlined"
                      defaultValue={getMoveAddress}
                      onChange={(evt) => setGetMoveAddress(evt.target.value)}
                      disabled={isReadOnly}
                      label="Indirizzo GET direzione tapparella (up, down)"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <ImportExportIcon />
                    </ListItemIcon>
                    <TextField
                      className={classes.textField}
                      variant="outlined"
                      defaultValue={setMoveAddress}
                      onChange={(evt) => setSetMoveAddress(evt.target.value)}
                      disabled={isReadOnly}
                      label="Indirizzo SET direzione tapparella (up, down)"
                    />
                  </ListItem>
                </>
              )}
              {type === DeviceType.FAN && (
                <>
                  <ListItem>
                    <ListItemIcon>
                      <ImportExportIcon />
                    </ListItemIcon>
                    <TextField
                      className={classes.textField}
                      variant="outlined"
                      defaultValue={setFanAddress}
                      onChange={(evt) => setSetFanAddress(evt.target.value)}
                      disabled={isReadOnly}
                      label="Indirizzo SET stato climatizzatore"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <ImportExportIcon />
                    </ListItemIcon>
                    <TextField
                      className={classes.textField}
                      variant="outlined"
                      defaultValue={setTemperatureAddressSummer}
                      onChange={(evt) =>
                        setSetTemperatureAddressSummer(evt.target.value)
                      }
                      disabled={isReadOnly}
                      label="Indirizzo SET temperatura ESTIVA climatizzatore"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <ImportExportIcon />
                    </ListItemIcon>
                    <TextField
                      className={classes.textField}
                      variant="outlined"
                      defaultValue={setTemperatureAddressWinter}
                      onChange={(evt) =>
                        setSetTemperatureAddressWinter(evt.target.value)
                      }
                      disabled={isReadOnly}
                      label="Indirizzo SET temperatura INVERNALE climatizzatore"
                    />
                  </ListItem>
                </>
              )}
            </>
          )}
        </>
      </List>
    </form>
  );
}
